import { useEffect, useState, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { Navbar } from '@/components/layout/Navbar';
import { ListView } from '@/components/list/ListView';
import { FilterBar } from '@/components/filters/FilterBar';
import { TimeframeMenu } from '@/components/ui/TimeframeMenu';
import { isDateMode } from '@/components/timeline/TimelineWeekView';
import type { SavedFilterState } from '@/services/api';
import {
  productApi,
  subProductApi,
  initiativeApi,
  classificationApi,
  statusApi,
  tagApi,
  userApi,
  fiscalYearApi,
  fieldConfigApi,
  labsApi,
} from '@/services/api';

type ViewMode = 'weekly' | 'monthly' | 'quarterly';

const VIEW_MODE_KEY = 'cpr_list_view_mode';

const TIMEFRAMES: { value: ViewMode; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

export function ListPage() {
  const {
    setProducts,
    setInitiatives,
    setClassifications,
    setStatuses,
    setTags,
    setOwners,
    setQuarters,
    setFieldConfigs,
    setLabsFeatures,
    setLoading,
    setError,
    refreshTrigger,
    activeSavedView,
  } = useAppStore();

  const [filteredInitiatives, setFilteredInitiatives] = useState<any[]>([]);
  const viewContainerRef = useRef<HTMLDivElement>(null);

  // Pre-filter from a Dashboard bar-chart click (?product=<id>[,<id>...]) — takes priority over a saved view.
  const [searchParams] = useSearchParams();
  const productParam = searchParams.get('product');
  const productFilterState: SavedFilterState | null = useMemo(() => (
    productParam
      ? {
          searchQuery: '', quarters: [], products: productParam.split(','), subProducts: [],
          statuses: [], owners: [], classifications: [], tags: [], hasDependencies: false,
        }
      : null
  ), [productParam]);

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const stored = sessionStorage.getItem(VIEW_MODE_KEY);
    return (stored === 'weekly' || stored === 'monthly' || stored === 'quarterly') ? stored : 'monthly';
  });

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    sessionStorage.setItem(VIEW_MODE_KEY, mode);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        productsData,
        subProductsData,
        initiativesData,
        classificationsData,
        statusesData,
        tagsData,
        usersData,
        fiscalYearsData,
        fieldConfigData,
        labsData,
      ] = await Promise.all([
        productApi.getAll(),
        subProductApi.getAll(),
        initiativeApi.getAll(),
        classificationApi.getAll(),
        statusApi.getAll(),
        tagApi.getAll(),
        userApi.getAll(),
        fiscalYearApi.getAll(),
        fieldConfigApi.getAll().catch(() => []),
        labsApi.getAll().catch(() => []),
      ]);

      const productsWithSubProducts = productsData.map((product: any) => ({
        ...product,
        subProducts: subProductsData.filter(
          (sp: any) => sp.productId === product.id
        ),
      }));

      setProducts(productsWithSubProducts);
      setInitiatives(initiativesData);
      setClassifications(classificationsData);
      setStatuses(statusesData);
      setTags(tagsData);
      setOwners(usersData);
      if (fieldConfigData.length > 0) {
        setFieldConfigs(fieldConfigData);
      }
      if (labsData.length > 0) {
        setLabsFeatures(labsData);
      }

      const allQuarters = fiscalYearsData
        .flatMap((fy: any) => fy.quarters || [])
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder);

      setQuarters(allQuarters);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load roadmap data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  return (
    <div className="h-screen flex flex-col bg-background-primary">
      <Navbar />

      <main className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="px-3 sm:px-6 py-3 bg-background-primary flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <div className="flex-1 min-w-0">
              <FilterBar
                key={productParam ? `product-${productParam}` : (activeSavedView?.viewType === 'list' ? activeSavedView.id : 'default')}
                onFilteredInitiativesChange={setFilteredInitiatives}
                viewType="list"
                viewContainerRef={viewContainerRef}
                savedViewKey="list"
                viewMode={viewMode}
                weeklyDatedCount={viewMode === 'weekly' ? filteredInitiatives.filter(isDateMode).length : undefined}
                injectedFilterState={productFilterState ?? (activeSavedView?.viewType === 'list' ? activeSavedView.filterState : null)}
              />
            </div>
            <TimeframeMenu value={viewMode} options={TIMEFRAMES} onChange={handleViewModeChange} />
          </div>

          <div data-tour-id="list-content" className="flex-1 overflow-hidden">
            <ListView initiatives={filteredInitiatives} containerRef={viewContainerRef} viewMode={viewMode} />
          </div>
        </div>
      </main>
    </div>
  );
}
